import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Check } from "lucide-react";

/**
 * Searchable IANA time-zone dropdown.
 * Falls back to a curated common list when Intl.supportedValuesOf is unavailable.
 */
const FALLBACK_ZONES = [
  "UTC", "GMT",
  "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York",
  "America/Toronto", "America/Mexico_City", "America/Sao_Paulo", "America/Buenos_Aires",
  "Europe/London", "Europe/Dublin", "Europe/Lisbon", "Europe/Madrid", "Europe/Paris",
  "Europe/Berlin", "Europe/Rome", "Europe/Amsterdam", "Europe/Stockholm", "Europe/Warsaw",
  "Europe/Athens", "Europe/Bucharest", "Europe/Kyiv", "Europe/Moscow", "Europe/Istanbul",
  "Africa/Cairo", "Africa/Johannesburg", "Africa/Nairobi", "Africa/Lagos",
  "Asia/Dubai", "Asia/Riyadh", "Asia/Karachi", "Asia/Kolkata", "Asia/Colombo",
  "Asia/Kathmandu", "Asia/Dhaka", "Asia/Bangkok", "Asia/Jakarta", "Asia/Kuala_Lumpur",
  "Asia/Singapore", "Asia/Hong_Kong", "Asia/Shanghai", "Asia/Taipei", "Asia/Tokyo",
  "Asia/Seoul", "Asia/Manila", "Australia/Perth", "Australia/Sydney", "Australia/Melbourne",
  "Pacific/Auckland", "Pacific/Fiji", "Pacific/Honolulu",
];

function getAllZones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      const list = Intl.supportedValuesOf("timeZone");
      if (Array.isArray(list) && list.length) return list;
    }
  } catch {
    /* ignore */
  }
  return FALLBACK_ZONES;
}

function tzOffsetLabel(tz) {
  try {
    const opts = { timeZone: tz, timeZoneName: "shortOffset" };
    const parts = new Intl.DateTimeFormat("en-US", opts).formatToParts(new Date());
    const off = parts.find((p) => p.type === "timeZoneName");
    return off?.value || "";
  } catch {
    return "";
  }
}

export default function TimezoneSelect({ value, onChange, testId = "timezone-select", disabled = false }) {
  const zones = useMemo(() => getAllZones(), []);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return zones.slice(0, 200);
    return zones.filter((z) => z.toLowerCase().includes(needle)).slice(0, 200);
  }, [q, zones]);

  const currentLabel = value || "Choose your time zone";
  const currentOffset = value ? tzOffsetLabel(value) : "";

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-trigger`}
        className={`flex w-full items-center justify-between rounded-xl border border-border bg-card px-4 py-2.5 text-left text-sm outline-none transition-colors hover:border-primary focus:border-primary ${disabled ? "opacity-60" : ""}`}
      >
        <span className="min-w-0 truncate">
          {currentLabel}
          {currentOffset && <span className="ml-2 text-muted-foreground">{currentOffset}</span>}
        </span>
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute z-40 mt-2 w-full overflow-hidden rounded-xl border border-border bg-card shadow-lg" data-testid={`${testId}-menu`}>
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search cities or regions…"
              className="flex-1 bg-transparent text-sm outline-none"
              data-testid={`${testId}-input`}
            />
            {q && (
              <button type="button" onClick={() => setQ("")} className="text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <ul className="max-h-64 overflow-y-auto py-1" role="listbox">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-muted-foreground">No match. Try “Asia/Dubai”, “Europe/London”…</li>
            ) : filtered.map((z) => (
              <li key={z}>
                <button
                  type="button"
                  onClick={() => { onChange(z); setOpen(false); setQ(""); }}
                  data-testid={`${testId}-option-${z}`}
                  className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-muted ${value === z ? "bg-muted" : ""}`}
                >
                  <span className="truncate">
                    {z}
                    <span className="ml-2 text-xs text-muted-foreground">{tzOffsetLabel(z)}</span>
                  </span>
                  {value === z && <Check className="h-3.5 w-3.5 text-brand-teal" />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
